import React, { useCallback, useEffect } from 'react';
import { Logo } from 'components/src/svg';
import { Maybe, NavbarDropdownMenuFieldsFragment, NavbarFieldsFragment, LinkFieldsFragment } from 'types/generated';
import NavbarChild from './NavbarChild';
import Link from 'next/link';
import Search from 'components/src/svg/Search';
import Email from 'components/src/svg/Email';
import Account from 'components/src/svg/Account';
import { colors } from 'components/src/foundation';
import NavbarDropdownMenu from './NavbarDropdownMenu';
import OutsideClickHandler from '@/components/common/OutsideClickHandler';
import { AnimatePresence, motion } from 'framer-motion';
import Menu from 'components/src/svg/Menu';
import MobileNavLinks from './MobileNavLinks';
import DoBusinessWithUsForm from '@/components/Contact/DoBusinessWithUsForm';
import { useAtom } from 'jotai';
import {
  cartQuantityAtom,
  isCallMeModalOpenAtom,
  isDoBusinessWithUsFormAtom,
  fullscreenBoxAtom,
  newItemAddedToCartAtom,
} from '@/store';
import clsx from 'clsx';
import LightBox from '@/components/common/LightBox';
import CallMeForm from '@/components/Contact/CallMeForm';
import Cart from 'components/src/svg/Cart';
import { useRouter } from 'next/router';
import NavbarAddedToCartBox from './NavBarAddedToCartBox';

type Props = {
  navbar: NavbarFieldsFragment;
  openDropdownTitle: string | null | undefined;
  onSetOpenDropdownTitle: (title: string | null | undefined) => void;
  contactFormSetting?: Maybe<string>;
};

const Navbar = ({ navbar, openDropdownTitle, contactFormSetting, onSetOpenDropdownTitle }: Props) => {
  const [isDoBusinessWithUsForm, setIsDoBusinessWithUsForm] = useAtom(isDoBusinessWithUsFormAtom);
  const [isCallMeModalOpen, setIsCallMeModalOpen] = useAtom(isCallMeModalOpenAtom);
  const [isFullscreenBox] = useAtom(fullscreenBoxAtom);
  const [cartQuantity] = useAtom(cartQuantityAtom);
  const [newItemAddedToCart, setNewItemAddedToCart] = useAtom(newItemAddedToCartAtom);
  const [scrolled, setScrolled] = React.useState(0);
  const [hideNavbar, setHideNavbar] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState<NavbarDropdownMenuFieldsFragment | undefined>(undefined);
  const [showMobileMenu, setShowMobileMenu] = React.useState<boolean>(false);
  const [showAddedtoCartBox, setShowAddedtoCartBox] = React.useState<boolean>(false);
  const router = useRouter();
  const cartPath = '/vefverslun/karfa';
  const isCartOpen = router.asPath.indexOf(cartPath) === 0;

  const onInterceptSetOpenDrownDownTitle = (title: string | null | undefined) => {
    if (title) {
      if (activeDropdown?.title === title) {
        setActiveDropdown(undefined);
        onSetOpenDropdownTitle(undefined);
        return;
      }
      const dropDownToOpen = navbar.childrenCollection?.items
        ?.filter((item) => item?.__typename === 'NavbarDropdownMenu')
        .find((child) => child && 'title' in child && child?.title === title);
      if (!dropDownToOpen || dropDownToOpen.__typename !== 'NavbarDropdownMenu') {
        setActiveDropdown(undefined);
        onSetOpenDropdownTitle(undefined);
        return;
      }
      setActiveDropdown(dropDownToOpen);
    } else {
      setActiveDropdown(undefined);
    }
    onSetOpenDropdownTitle(title);
    setShowAddedtoCartBox(false);
  };

  const toggleMobileMenu = () => {
    if (activeDropdown && showMobileMenu) {
      onInterceptSetOpenDrownDownTitle(null);
    }
    setShowAddedtoCartBox(false);
    setShowMobileMenu(!showMobileMenu);
  };

  const handleOutsideClick = () => {
    if (activeDropdown) {
      onSetOpenDropdownTitle(null);
      setActiveDropdown(undefined);
    }

    if (showAddedtoCartBox) {
      setShowAddedtoCartBox(false);
    }

    setShowMobileMenu(false);
  };

  const scrollHandler = useCallback(() => {
    if (window.scrollY > scrolled && window.innerWidth <= 1023 && window.scrollY > 10) {
      if (!showMobileMenu) {
        setHideNavbar(true);
        setShowAddedtoCartBox(false);
      }
    } else {
      setHideNavbar(false);
    }
    setScrolled(window.scrollY);
  }, [scrolled, showMobileMenu, setHideNavbar]);

  const resizeHandler = useCallback(() => {
    if (showMobileMenu && window.innerWidth > 1023) {
      setShowMobileMenu(false);
    }
    if ((activeDropdown || openDropdownTitle) && window.innerWidth <= 1023) {
      setActiveDropdown(undefined);
      onSetOpenDropdownTitle(undefined);
    }
  }, [showMobileMenu, activeDropdown, openDropdownTitle, setShowMobileMenu, setActiveDropdown, onSetOpenDropdownTitle]);

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, [scrollHandler]);

  useEffect(() => {
    if (newItemAddedToCart && !showAddedtoCartBox) {
      setShowAddedtoCartBox(true);
      setHideNavbar(false);
      setNewItemAddedToCart(false);
    } else if (newItemAddedToCart && showAddedtoCartBox) {
      setNewItemAddedToCart(false);
    }
  }, [newItemAddedToCart, setNewItemAddedToCart, showAddedtoCartBox]);

  // Disable body scroll when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [showMobileMenu]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [resizeHandler]);

  const mobileNavLinksRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <motion.div
        className={`fixed top-0 z-[100] col-span-full max-h-100 w-full max-w-full transform`}
        initial={{ top: hideNavbar ? '' : -128 }}
        animate={{ top: hideNavbar ? -128 : '' }}
        transition={{ type: 'tween', ease: 'easeIn', duration: 0.3 }}
      >
        <div className="pointer-events-none col-span-full grid w-full grid-cols-root gap-x-16 md:grid-cols-root-md md:gap-x-24">
          <nav
            className={clsx(
              'pointer-events-auto col-span-12 mx-16 my-8 overflow-hidden rounded-[28px] p-lg md:col-start-2 md:-mx-16',
              {
                'items-start gap-sm': showMobileMenu,
                'bg-transparent': isFullscreenBox && !activeDropdown,
                'bg-white-50': !isFullscreenBox || activeDropdown,
              }
            )}
            style={
              !isFullscreenBox
                ? {
                    boxShadow:
                      '0px 29px 64px 0px #D1001F1A, 0px 116px 116px 0px #D1001F17, 0px 262px 157px 0px #D1001F0D, 0px 465px 186px 0px #D1001F03, 0px 727px 203px 0px #D1001F00',
                  }
                : undefined
            }
          >
            <OutsideClickHandler exceptionRef={mobileNavLinksRef} onClick={handleOutsideClick}>
              <div className={`${showMobileMenu ? 'w-full' : ''}  flex justify-between px-8`}>
                <Link href="/">
                  <Logo size={50} variant={`${isFullscreenBox && !activeDropdown ? 'inverted' : 'classic'}`} />
                </Link>
                <div className="flex items-center justify-center gap-xs py-4">
                  {!showMobileMenu && (
                    <div className={`hidden items-center lg:flex`}>
                      {navbar.childrenCollection?.items?.map((child, index) => (
                        <NavbarChild
                          key={index}
                          childData={child as LinkFieldsFragment | NavbarDropdownMenuFieldsFragment | null}
                          openDropdownTitle={openDropdownTitle}
                          setOpenDropdownTitle={onInterceptSetOpenDrownDownTitle}
                          showMobileMenu={showMobileMenu}
                          transparent={isFullscreenBox && !activeDropdown}
                        />
                      ))}
                    </div>
                  )}
                  <div className="flex gap-4">
                    <Link href="/leit" className="flex h-40 w-40 items-center justify-center rounded-full">
                      <Search color={isFullscreenBox && !activeDropdown ? colors.primary.white : colors.neutral[10]} />
                    </Link>
                    <Link
                      href="https://postur.internet.is/postur/"
                      className="flex h-40 w-40 items-center justify-center rounded-full"
                    >
                      <Email color={isFullscreenBox && !activeDropdown ? colors.primary.white : colors.neutral[10]} />
                    </Link>
                    <motion.div key={cartQuantity} initial={{ scale: 1.2 }} animate={{ scale: 1 }}>
                      <Link
                        id="store-cart-position"
                        href="/vefverslun/karfa"
                        className={`relative flex h-40 w-40 items-center justify-center rounded-full ${
                          isCartOpen ? 'bg-red-100' : ''
                        }`}
                      >
                        <Cart
                          color={
                            (isFullscreenBox && !activeDropdown) || isCartOpen
                              ? colors.primary.white
                              : colors.neutral[10]
                          }
                        />
                        {cartQuantity > 0 && (
                          <div
                            className={`absolute right-0 top-0 flex h-16 w-16 items-center justify-center rounded-full text-[9px] ${
                              isCartOpen ? 'bg-white-100 text-red-100 shadow-sm' : 'bg-red-100 text-white-75'
                            }`}
                          >
                            {cartQuantity < 10 ? cartQuantity : '9+'}
                          </div>
                        )}
                      </Link>
                    </motion.div>
                    <Link
                      href="https://minar.syn.is/innskraning/eid"
                      className="flex h-40 w-40 items-center justify-center rounded-full"
                    >
                      <Account color={isFullscreenBox && !activeDropdown ? colors.primary.white : colors.neutral[10]} />
                    </Link>
                    <div onClick={toggleMobileMenu} className="flex cursor-pointer items-center lg:hidden">
                      <Menu fill={isFullscreenBox && !activeDropdown ? '#FEF4F5' : '#F14157'} isOpen={showMobileMenu} />
                    </div>
                  </div>
                </div>
              </div>
              <AnimatePresence>
                {showMobileMenu && (
                  <MobileNavLinks
                    ref={mobileNavLinksRef}
                    data={navbar}
                    openDropdownTitle={openDropdownTitle}
                    activeDropdown={activeDropdown}
                    setOpenDropdownTitle={onInterceptSetOpenDrownDownTitle}
                  />
                )}
              </AnimatePresence>
              {!showMobileMenu && (
                <AnimatePresence>
                  {!!activeDropdown && (
                    <NavbarDropdownMenu
                      data={activeDropdown}
                      openDropdownTitle={openDropdownTitle}
                      setOpenDropdownTitle={onInterceptSetOpenDrownDownTitle}
                      showMobileMenu={showMobileMenu}
                    />
                  )}
                </AnimatePresence>
              )}
            </OutsideClickHandler>
          </nav>
          <AnimatePresence mode="popLayout">
            {showAddedtoCartBox && <NavbarAddedToCartBox onClose={() => setShowAddedtoCartBox(false)} />}
          </AnimatePresence>
        </div>
      </motion.div>
      {navbar.contactForm && (
        <LightBox isVisible={isDoBusinessWithUsForm}>
          <DoBusinessWithUsForm
            data={navbar.contactForm}
            onClose={() => setIsDoBusinessWithUsForm(false)}
            contactFormSetting={contactFormSetting}
          />
        </LightBox>
      )}
      {navbar.callMeForm && (
        <LightBox isVisible={isCallMeModalOpen}>
          <CallMeForm data={navbar.callMeForm} onClose={() => setIsCallMeModalOpen(false)} />
        </LightBox>
      )}
    </>
  );
};

export default Navbar;
